import { useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { useArtifacts } from '@hooks/storeHooks/useArtifacts';
import { useSessionFeatureState } from '@hooks/storeHooks/useSessionState';
import type { MentorBase } from '../../../types/mentor';
import { UpdateBalanceEventData } from '../../../types/webRtcMessage';
import {
  MUSIC_ALBUM_COVER_REGENERATION,
  MUSIC_LYRICS_REGENERATION,
  MUSIC_SONG_TITLE_REGENERATION,
  REGENERATE_CREATION,
} from '../constants/musicGenerationRealtimeTools';
interface UseWebRTCMessageHandlerProps {
  startParam: string | null;
  sendMessage: (data: any) => void;
  handleRegenerateCreation?: (
    args: {
      type: 'lyrics' | 'title' | 'cover' | 'music' | 'image';
      modification_request: string;
    },
    sendFn: (msg: string) => void,
  ) => Promise<void>;
}

export const useWebRTCMessageHandler = ({
  startParam,
  sendMessage,
  handleRegenerateCreation,
}: UseWebRTCMessageHandlerProps) => {
  const navigate = useNavigate();
  const location = useLocation();

  const { setSuggestedMentors } = useArtifacts();

  const {
    setTranscriptTime,
    setAvatarReady,
    setIsConnecting,
    setSecondsBalance,
    setCreditBalance,
    isSessionPage,
    taskId,
    companionData,
  } = useSessionFeatureState();

  const isMusicGenerationSession = !!taskId;

  const handleWebRTCMessage = useCallback(
    (data: any) => {
      switch (data.event) {
        case 'avatar_state_changed':
          console.log('Avatar state changed:', data.data?.state);
          if (data.data?.state === 'ready') {
            setAvatarReady(true);
            setIsConnecting(false);

            if (isSessionPage && !startParam) {
              // Add ?start=true to URL when avatar is ready on session page
              const currentParams = new URLSearchParams(location.search);
              currentParams.set('start', 'true');
              const newUrl = `${location.pathname}?${currentParams.toString()}`;
              navigate(newUrl, { replace: true });
            }
          }
          break;
        case 'message_received': {
          const { message } = data.data || {};
          if (
            message?.action === 'completed' &&
            typeof message.content === 'string' &&
            message.role
          ) {
            console.log('Message received:', message.content);
            setTranscriptTime(Date.now());
          }
          if (
            (message?.type === 'function_call_output' ||
              message?.type === 'function_call') &&
            message?.content
          ) {
            try {
              const parsed = JSON.parse(message.content);
              console.log('Function call output:', parsed);
            } catch (error) {
              console.error(
                'Failed to parse function call content:',
                error,
                'Content:',
                message.content,
              );
            }
          }
          break;
        }
        case 'function_implicitly_called': {
          console.log('function_implicitly_called', data);
          const callId = data?.data?.call_id;
          const functionName = data?.data?.name;
          const modificationRequest =
            data?.data?.arguments?.modification_request;
          const regenTypeArg = data?.data?.arguments?.type as
            | 'lyrics'
            | 'title'
            | 'cover'
            | 'music'
            | 'image'
            | undefined;

          if (!callId) {
            break;
          }

          const sendFunctionOutput = (cid: string, output: unknown) => {
            const responseData = {
              type: 'send_function_output',
              data: {
                call_id: cid,
                output: { content: output },
              },
            };
            console.log('sending function output', responseData);
            sendMessage(responseData);
          };

          const sendOut = (text: string) => sendFunctionOutput(callId, text);

          if (functionName === MUSIC_LYRICS_REGENERATION) {
            if (
              handleRegenerateCreation &&
              isMusicGenerationSession &&
              modificationRequest
            ) {
              void handleRegenerateCreation(
                { type: 'lyrics', modification_request: modificationRequest },
                sendOut,
              );
            } else {
              sendFunctionOutput(callId, 'Continue to support the user.');
            }
            break;
          }

          if (functionName === MUSIC_ALBUM_COVER_REGENERATION) {
            if (
              handleRegenerateCreation &&
              isMusicGenerationSession &&
              modificationRequest
            ) {
              void handleRegenerateCreation(
                { type: 'cover', modification_request: modificationRequest },
                sendOut,
              );
            } else {
              sendFunctionOutput(callId, 'Continue to support the user.');
            }
            break;
          }

          if (functionName === MUSIC_SONG_TITLE_REGENERATION) {
            if (
              handleRegenerateCreation &&
              isMusicGenerationSession &&
              modificationRequest
            ) {
              void handleRegenerateCreation(
                { type: 'title', modification_request: modificationRequest },
                sendOut,
              );
            } else {
              sendFunctionOutput(callId, 'Continue to support the user.');
            }
            break;
          }

          if (functionName === REGENERATE_CREATION) {
            if (
              handleRegenerateCreation &&
              regenTypeArg &&
              modificationRequest
            ) {
              void handleRegenerateCreation(
                {
                  type: regenTypeArg,
                  modification_request: modificationRequest,
                },
                sendOut,
              );
            } else {
              sendFunctionOutput(callId, 'Continue to support the user.');
            }
          }
          break;
        }
        case 'function_notification':
          {
            const notificationType = data.data?.type;
            if (notificationType === 'edit_mode_state_changed') {
              const enabled = data.data?.data?.enabled;
              if (enabled === true) {
                console.log(
                  '%c[WebRTC] ✅ Edit mode STARTED successfully',
                  'color: #00ff00; ',
                  data,
                );
              } else if (enabled === false) {
                console.log(
                  '%c[WebRTC] ✅ Edit mode STOPPED successfully',
                  'color: #00aaff; ',
                  data,
                );
              }
            }
          }
          break;
        case 'back_channel_message_received':
          {
            const type = data.data?.type;
            if (type === 'update-balance') {
              console.log('%c=====================>', 'color: #0062ff');
              console.log('', data.data);
              console.log('%c=====================>', 'color: #0062ff');

              const balanceData = data.data
                ?.data as unknown as UpdateBalanceEventData;
              if (balanceData?.credit_usage) {
                const newSecondsBalance =
                  (balanceData.credit_usage.subscription_details?.balance ??
                    0) + (balanceData.credit_usage.pack_details?.balance ?? 0);
                setSecondsBalance(newSecondsBalance);

                const newCreditBalance =
                  (balanceData.credit_usage.subscription_details
                    ?.creditBalance ?? 0) +
                  (balanceData.credit_usage.pack_details?.creditBalance ?? 0);
                setCreditBalance(newCreditBalance);
              }
            }
            if (type === 'suggested-mentors') {
              const mentorsData = data.data?.data as unknown as MentorBase[];
              setSuggestedMentors(
                mentorsData.filter(
                  (mentor: MentorBase) => mentor.mentorId !== companionData?.id,
                ),
              );
            }
          }
          break;
      }
    },
    [
      setAvatarReady,
      setIsConnecting,
      isSessionPage,
      startParam,
      companionData?.id,
      location,
      navigate,
      isMusicGenerationSession,
      setSecondsBalance,
      setCreditBalance,
      setSuggestedMentors,
      sendMessage,
      handleRegenerateCreation,
      setTranscriptTime,
    ],
  );

  return { handleWebRTCMessage };
};
